import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import templateService from '../../../services/template.service';
import clinicService from '../../../services/clinic.service';
import Loading from '../../common/Loading';
import Swal from '../../helpers/swal';
import TabNavItem from '../../common/tabs/TabNavItem';
import TabContent from '../../common/tabs/TabContent';

const STAT_CONFIG = [
    { key: 'sms', label: 'SMS', icon: 'bi-chat-dots', iconClass: 'admin-templates-stat__icon--sms' },
    { key: 'email', label: 'Email', icon: 'bi-envelope', iconClass: 'admin-templates-stat__icon--email' },
    { key: 'call', label: 'Call', icon: 'bi-telephone', iconClass: 'admin-templates-stat__icon--call' },
    { key: 'total', label: 'Total', icon: 'bi-collection', iconClass: 'admin-templates-stat__icon--total' },
];

const TAB_CONFIG = [
    { id: 'sms-tab', key: 'sms', title: 'SMS Templates', icon: 'bi bi-chat-dots' },
    { id: 'email-tab', key: 'email', title: 'Email Templates', icon: 'bi bi-envelope' },
    { id: 'call-tab', key: 'call', title: 'Call Templates', icon: 'bi bi-telephone' },
];

const tabIdFromKey = (key) => TAB_CONFIG.find((tab) => tab.key === key)?.id || 'sms-tab';

const ClinicTemplateDashboard = () => {
    const { clinicId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [clinics, setClinics] = useState([]);
    const [smsTemplates, setSmsTemplates] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [callTemplates, setCallTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const audioRef = useRef(null);
    const [audioBlobUrls, setAudioBlobUrls] = useState({});

    const selectedClinic = clinicId || '';
    const tabParam = searchParams.get('tab');
    const activeTab = tabIdFromKey(['sms', 'email', 'call'].includes(tabParam) ? tabParam : 'sms');

    const setActiveTab = useCallback(
        (tabId) => {
            const tabKey = TAB_CONFIG.find((tab) => tab.id === tabId)?.key || 'sms';
            setSearchParams(tabKey === 'sms' ? {} : { tab: tabKey }, { replace: true });
        },
        [setSearchParams]
    );

    useEffect(() => {
        const fetchClinics = async () => {
            try {
                setLoading(true);
                const response = await clinicService.getAllClinicsFast();
                setClinics(response.data);
            } catch (error) {
                console.error('Error fetching clinics:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load clinics. Please try again.',
                    confirmButtonColor: '#dc3545',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchClinics();
    }, []);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!selectedClinic) {
                setSmsTemplates([]);
                setEmailTemplates([]);
                setCallTemplates([]);
                return;
            }

            try {
                setTemplatesLoading(true);

                const [smsResponse, emailResponse, callResponse] = await Promise.all([
                    templateService.getSmsTemplates(selectedClinic),
                    templateService.getEmailTemplates(selectedClinic),
                    templateService.getCallTemplates(selectedClinic),
                ]);

                setSmsTemplates(smsResponse.data);
                setEmailTemplates(emailResponse.data);
                setCallTemplates(callResponse.data);
            } catch (error) {
                console.error('Error fetching templates:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load templates. Please try again.',
                    confirmButtonColor: '#dc3545',
                });
                setSmsTemplates([]);
                setEmailTemplates([]);
                setCallTemplates([]);
            } finally {
                setTemplatesLoading(false);
            }
        };

        fetchTemplates();
    }, [selectedClinic]);

    useEffect(() => {
        return () => {
            Object.values(audioBlobUrls).forEach((url) => {
                URL.revokeObjectURL(url);
            });
        };
    }, [audioBlobUrls]);

    const clearAudioUrls = () => {
        Object.values(audioBlobUrls).forEach((url) => {
            URL.revokeObjectURL(url);
        });
        setAudioBlobUrls({});
        setCurrentlyPlaying(null);
    };

    const handleClinicChange = (event) => {
        const nextClinicId = event.target.value;
        clearAudioUrls();

        if (nextClinicId) {
            const tabKey = TAB_CONFIG.find((tab) => tab.id === activeTab)?.key;
            const tabQuery = tabKey && tabKey !== 'sms' ? `?tab=${tabKey}` : '';
            navigate(`/clinic-templates/${nextClinicId}${tabQuery}`);
        } else {
            navigate('/clinic-templates');
        }
    };

    const handleEditTemplate = (template, type) => {
        if (type === 'sms') {
            navigate(`/clinic-templates/${selectedClinic}/edit-sms/${template.id}`);
        } else if (type === 'call') {
            navigate(`/clinic-templates/${selectedClinic}/edit-call/${template.id}`);
        } else if (type === 'email') {
            navigate(`/clinic-templates/${selectedClinic}/edit-email/${template.id}`);
        } else {
            Swal.fire({
                icon: 'info',
                title: `Edit ${type.toUpperCase()} Template`,
                text: `Edit functionality for template: ${template.reminderName || 'Unnamed Template'}`,
                confirmButtonColor: '#0d6efd',
            });
        }
    };

    const handlePlayAudio = async (template) => {
        try {
            if (currentlyPlaying === template.id) {
                if (audioRef.current.paused) {
                    audioRef.current.play();
                } else {
                    audioRef.current.pause();
                }
                return;
            }

            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }

            if (audioBlobUrls[template.id]) {
                audioRef.current.src = audioBlobUrls[template.id];
                audioRef.current.play().catch((error) => {
                    console.error('Error playing audio:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Playback Error',
                        text: 'Could not play the audio file.',
                        confirmButtonColor: '#dc3545',
                    });
                });
                setCurrentlyPlaying(template.id);
                return;
            }

            const response = await templateService.playAudio(selectedClinic, template.id);
            const blob = new Blob([response.data], { type: 'audio/mpeg' });
            const blobUrl = URL.createObjectURL(blob);

            setAudioBlobUrls((prev) => ({
                ...prev,
                [template.id]: blobUrl,
            }));

            audioRef.current.src = blobUrl;
            audioRef.current.play().catch((error) => {
                console.error('Error playing audio:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Playback Error',
                    text: 'Could not play the audio file.',
                    confirmButtonColor: '#dc3545',
                });
                setCurrentlyPlaying(null);
            });

            setCurrentlyPlaying(template.id);
        } catch (error) {
            console.error('Error playing audio:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load audio. Please try again.',
                confirmButtonColor: '#dc3545',
            });
        }
    };

    const handleAudioEnded = () => {
        setCurrentlyPlaying(null);
    };

    const getTemplateStats = (templates) => ({
        total: templates.length,
        active: templates.filter((t) => t.status).length,
        inactive: templates.filter((t) => !t.status).length,
    });

    const smsStats = getTemplateStats(smsTemplates);
    const emailStats = getTemplateStats(emailTemplates);
    const callStats = getTemplateStats(callTemplates);

    const templateCounts = {
        sms: smsStats.total,
        email: emailStats.total,
        call: callStats.total,
        total: smsStats.total + emailStats.total + callStats.total,
    };

    const selectedClinicName = clinics.find((c) => c.id === selectedClinic)?.name;

    const getTemplatesForTab = (tabKey) => {
        switch (tabKey) {
            case 'sms':
                return smsTemplates;
            case 'email':
                return emailTemplates;
            case 'call':
                return callTemplates;
            default:
                return [];
        }
    };

    const getStatsForTab = (tabKey) => {
        switch (tabKey) {
            case 'sms':
                return smsStats;
            case 'email':
                return emailStats;
            case 'call':
                return callStats;
            default:
                return { total: 0, active: 0, inactive: 0 };
        }
    };

    const renderTemplateTable = (tabKey) => {
        const templates = getTemplatesForTab(tabKey);
        const stats = getStatsForTab(tabKey);

        if (!selectedClinic) {
            return (
                <div className="install-templates-empty">
                    <i className="bi bi-building"></i>
                    <h5>Select a Clinic</h5>
                    <p>Choose a clinic from the dropdown above to view its templates.</p>
                </div>
            );
        }

        if (templatesLoading) {
            return (
                <div className="text-center py-5">
                    <Loading />
                    <p className="text-muted mt-2 mb-0">Loading templates...</p>
                </div>
            );
        }

        if (templates.length === 0) {
            const tabLabel = TAB_CONFIG.find((tab) => tab.key === tabKey)?.title || 'Templates';
            return (
                <div className="install-templates-empty">
                    <i className={`bi ${TAB_CONFIG.find((tab) => tab.key === tabKey)?.icon?.split(' ')[1] || 'bi-inbox'}`}></i>
                    <h5>No {tabLabel} Found</h5>
                    <p>
                        {selectedClinicName
                            ? `${selectedClinicName} doesn't have any ${tabLabel.toLowerCase()} yet.`
                            : `This clinic doesn't have any ${tabLabel.toLowerCase()} yet.`}
                    </p>
                </div>
            );
        }

        return (
            <>
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table admin-templates-table">
                        <thead>
                            <tr>
                                <th>
                                    <i className="bi bi-toggle-on"></i> Status
                                </th>
                                <th>
                                    <i className="bi bi-bookmark"></i> Reminder Name
                                </th>
                                <th>
                                    <i className="bi bi-chat-left-text"></i>{' '}
                                    {tabKey === 'call' ? 'Script Content' : 'Template Content'}
                                </th>
                                {tabKey === 'email' && (
                                    <th>
                                        <i className="bi bi-eye"></i> Preview
                                    </th>
                                )}
                                {tabKey === 'call' && (
                                    <th>
                                        <i className="bi bi-volume-up"></i> Audio
                                    </th>
                                )}
                                <th>
                                    <i className="bi bi-clock-history"></i> Modified
                                </th>
                                <th className="text-end">
                                    <i className="bi bi-gear"></i> Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map((template) => (
                                <tr key={template.id}>
                                    <td>
                                        <span
                                            className={`badge ${template.status ? 'bg-success' : 'bg-secondary'}`}
                                        >
                                            {template.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="fw-semibold">
                                            {template.reminderName || 'Unnamed Template'}
                                        </div>
                                        <small className="text-muted font-monospace">ID: {template.id}</small>
                                    </td>
                                    <td>
                                        {tabKey === 'sms' || tabKey === 'call' ? (
                                            <div
                                                className="admin-templates-detail-content"
                                                style={{ maxHeight: '100px' }}
                                            >
                                                {template.content}
                                            </div>
                                        ) : (
                                            <div
                                                className="admin-templates-content-preview"
                                                title={template.content}
                                            >
                                                {template.content?.startsWith('http')
                                                    ? 'HTML template stored externally'
                                                    : template.content || '—'}
                                            </div>
                                        )}
                                    </td>
                                    {tabKey === 'email' && (
                                        <td>
                                            {template.content?.startsWith('http') ? (
                                                <a
                                                    href={template.content}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <i className="bi bi-eye"></i> Preview
                                                </a>
                                            ) : (
                                                <span className="text-muted small">—</span>
                                            )}
                                        </td>
                                    )}
                                    {tabKey === 'call' && (
                                        <td>
                                            {template.audioPath ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button
                                                        variant={
                                                            currentlyPlaying === template.id
                                                                ? 'warning'
                                                                : 'success'
                                                        }
                                                        size="sm"
                                                        onClick={() => handlePlayAudio(template)}
                                                        title={
                                                            currentlyPlaying === template.id
                                                                ? 'Pause Audio'
                                                                : 'Play Audio'
                                                        }
                                                    >
                                                        <i
                                                            className={`bi ${
                                                                currentlyPlaying === template.id
                                                                    ? 'bi-pause'
                                                                    : 'bi-play'
                                                            }`}
                                                        ></i>
                                                    </Button>
                                                    <small className="text-muted">Audio available</small>
                                                </div>
                                            ) : (
                                                <span className="text-muted small">No audio</span>
                                            )}
                                        </td>
                                    )}
                                    <td>
                                        <small className="text-muted">{template.modifiedAt || '—'}</small>
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleEditTemplate(template, tabKey)}
                                            title={`Edit ${tabKey.toUpperCase()} Template`}
                                        >
                                            <i className="bi bi-pencil"></i> Edit
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3 border-top">
                    <span className="text-muted small">
                        Showing {stats.total} {tabKey} template{stats.total !== 1 ? 's' : ''}
                        {selectedClinicName ? ` for ${selectedClinicName}` : ''}
                    </span>
                    <div className="d-flex gap-2">
                        <span className="badge bg-success">{stats.active} active</span>
                        <span className="badge bg-secondary">{stats.inactive} inactive</span>
                    </div>
                </div>
            </>
        );
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="pagetitle">
                    <h1>
                        <i className="bi bi-chat-dots"></i> Clinic Templates
                    </h1>
                </div>
                <Loading />
            </div>
        );
    }

    // Clinic selection page — no clinic in the URL yet
    if (!selectedClinic) {
        return (
            <div className="container-fluid">
                <div className="pagetitle">
                    <h1>
                        <i className="bi bi-chat-dots"></i> Clinic Templates
                    </h1>
                </div>

                <Card className="admin-templates-card shadow-sm border-0">
                    <Card.Body className="p-3">
                        <div className="row g-3">
                            <div className="col-lg-8">
                                <div className="notifications-panel install-templates-clinic-panel">
                                    <div className="notifications-panel__header">
                                        <i className="bi bi-building"></i>
                                        Select Clinic
                                    </div>
                                    <div className="notifications-panel__body">
                                        <label htmlFor="clinic-template-select" className="notifications-form-label">
                                            Choose clinic
                                        </label>
                                        <select
                                            id="clinic-template-select"
                                            className="form-select"
                                            value=""
                                            onChange={handleClinicChange}
                                        >
                                            <option value="">Select a clinic</option>
                                            {clinics.map((clinic) => (
                                                <option key={clinic.id} value={clinic.id}>
                                                    {clinic.name}
                                                    {clinic.city ? ` — ${clinic.city}` : ''}
                                                    {clinic.number ? ` (#${clinic.number})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="notifications-form-hint mb-0">
                                            Select a clinic to open its SMS, email, and call templates.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="install-templates-summary-panel">
                                    <div className="install-templates-summary-panel__label">Clinics</div>
                                    <div className="install-templates-summary-panel__value">
                                        {clinics.length}
                                    </div>
                                    <div className="install-templates-summary-panel__meta">available</div>
                                    <div className="install-templates-summary-panel__clinic install-templates-summary-panel__clinic--empty">
                                        No clinic selected
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="install-templates-empty mt-4">
                            <i className="bi bi-building"></i>
                            <h5>Select a Clinic</h5>
                            <p>Choose a clinic above to view and manage its templates.</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const activeTabKey = TAB_CONFIG.find((tab) => tab.id === activeTab)?.key || 'sms';
    const activeTabStats = getStatsForTab(activeTabKey);

    return (
        <div className="container-fluid">
            <audio
                ref={audioRef}
                onEnded={handleAudioEnded}
                onPause={() => {
                    if (audioRef.current && audioRef.current.ended) {
                        setCurrentlyPlaying(null);
                    }
                }}
            />

            <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h1>
                    <i className="bi bi-chat-dots"></i>{' '}
                    {selectedClinicName || 'Clinic Templates'}
                </h1>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => navigate('/clinic-templates')}
                >
                    <i className="bi bi-chevron-left"></i> Change Clinic
                </Button>
            </div>

            <Card className="admin-templates-card shadow-sm border-0">
                <Card.Body className="p-3">
                    <div className="admin-templates-stat-grid">
                        {STAT_CONFIG.map((stat) => (
                            <div key={stat.key} className="admin-templates-stat">
                                <div className="admin-templates-stat__info">
                                    <div className="admin-templates-stat__value">
                                        {templateCounts[stat.key]}
                                    </div>
                                    <div className="admin-templates-stat__label">{stat.label}</div>
                                </div>
                                <span className={`admin-templates-stat__icon ${stat.iconClass}`}>
                                    <i className={`bi ${stat.icon}`}></i>
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-lg-8">
                            <div className="notifications-panel install-templates-clinic-panel">
                                <div className="notifications-panel__header">
                                    <i className="bi bi-building"></i>
                                    Clinic
                                </div>
                                <div className="notifications-panel__body">
                                    <label htmlFor="clinic-template-select" className="notifications-form-label">
                                        Switch clinic
                                    </label>
                                    <select
                                        id="clinic-template-select"
                                        className="form-select"
                                        value={selectedClinic}
                                        onChange={handleClinicChange}
                                    >
                                        <option value="">Select a clinic</option>
                                        {clinics.map((clinic) => (
                                            <option key={clinic.id} value={clinic.id}>
                                                {clinic.name}
                                                {clinic.city ? ` — ${clinic.city}` : ''}
                                                {clinic.number ? ` (#${clinic.number})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="notifications-form-hint mb-0">
                                        Viewing templates for the selected clinic. Edit a template, then use Back
                                        to return here with this clinic still selected.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="install-templates-summary-panel">
                                <div className="install-templates-summary-panel__label">Active tab</div>
                                <div className="install-templates-summary-panel__value">
                                    {activeTabStats.total}
                                </div>
                                <div className="install-templates-summary-panel__meta">
                                    {TAB_CONFIG.find((tab) => tab.id === activeTab)?.title?.toLowerCase() ||
                                        'templates'}
                                </div>
                                {selectedClinicName ? (
                                    <div className="install-templates-summary-panel__clinic">
                                        <i className="bi bi-hospital me-1"></i>
                                        {selectedClinicName}
                                    </div>
                                ) : (
                                    <div className="install-templates-summary-panel__clinic install-templates-summary-panel__clinic--empty">
                                        No clinic selected
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3 nav-tabs-bordered">
                        <ul className="nav nav-tabs flex-grow-1 mb-0 border-0">
                            {TAB_CONFIG.map((tab) => (
                                <TabNavItem
                                    key={tab.id}
                                    id={tab.id}
                                    title={`${tab.title} (${getStatsForTab(tab.key).total})`}
                                    icon={tab.icon}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                />
                            ))}
                        </ul>
                    </div>

                    <div className="tab-content">
                        {TAB_CONFIG.map((tab) => (
                            <TabContent key={tab.id} id={tab.id} activeTab={activeTab}>
                                {renderTemplateTable(tab.key)}
                            </TabContent>
                        ))}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default ClinicTemplateDashboard;
